import { useEffect, useRef, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { errMsg } from "@/lib/errors";
import { AtSign, Upload, ImagePlus, Trash2, ExternalLink, Mail, Check, X } from "lucide-react";
import { Link } from "react-router-dom";

export default function ProfileSettings() {
  const { user, refreshMe } = useAuth();
  const [displayName, setDisplayName] = useState(user?.display_name || "");
  const [bio, setBio] = useState(user?.bio || "");
  const [bannerPath, setBannerPath] = useState(user?.banner_path || "");
  const [busy, setBusy] = useState(false);
  const [emailOptin, setEmailOptin] = useState(true);
  const [handle, setHandle] = useState(user?.username || "");
  const [handleCheck, setHandleCheck] = useState({ ok: null, reason: "", current: false, busy: false, suggestion: "" });
  const [handleSaving, setHandleSaving] = useState(false);
  const bannerRef = useRef();

  // Load current email preference once — it's a rare toggle so no reason to
  // sync it via AuthContext.
  useEffect(() => {
    api.get("/user/email-prefs")
      .then((r) => setEmailOptin(r.data.email_updates_optin !== false))
      .catch(() => {});
  }, []);

  const setEmailPref = async (v) => {
    setEmailOptin(v);
    try { await api.post("/user/email-prefs", { email_updates_optin: v }); }
    catch (e) {
      setEmailOptin(!v);
      toast.error(errMsg(e));
    }
  };

  // Live availability check for the handle input — debounced so we don't hit
  // the API on every keystroke. Runs even when the input matches the current
  // handle so a user with a legacy invalid handle (e.g. "sylvester anthony iii")
  // sees the fix suggestion immediately on load.
  useEffect(() => {
    const trimmed = (handle || "").trim();
    if (!trimmed) {
      setHandleCheck({ ok: null, reason: "", current: false, busy: false, suggestion: "" });
      return;
    }
    setHandleCheck((s) => ({ ...s, busy: true }));
    const h = setTimeout(async () => {
      try {
        const { data } = await api.get(`/users/handle-available?username=${encodeURIComponent(trimmed)}`);
        setHandleCheck({
          ok: !!data.ok,
          reason: data.reason || "",
          current: !!data.current,
          busy: false,
          suggestion: data.suggestion || "",
        });
      } catch {
        setHandleCheck({ ok: null, reason: "Check failed", current: false, busy: false, suggestion: "" });
      }
    }, 350);
    return () => clearTimeout(h);
  }, [handle]);

  const saveHandle = async () => {
    const trimmed = (handle || "").trim().toLowerCase();
    if (!trimmed || trimmed === (user?.username || "")) return;
    if (!handleCheck.ok) { toast.error(handleCheck.reason || "Fix the handle first"); return; }
    setHandleSaving(true);
    try {
      await api.patch("/users/me/username", { username: trimmed });
      toast.success(`Handle updated to @${trimmed}`);
      refreshMe && refreshMe();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setHandleSaving(false); }
  };

  if (!user) return null;

  const uploadBanner = async () => {
    const file = bannerRef.current?.files?.[0];
    if (!file) return toast.error("Pick an image");
    if (file.size > 8 * 1024 * 1024) return toast.error("Max 8MB");
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setBannerPath(data.path);
      toast.success("Banner uploaded — hit Save to apply");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const clearBanner = () => { setBannerPath(""); toast.info("Banner cleared — hit Save to apply"); };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.patch("/artist/me", {
        display_name: displayName, bio, banner_path: bannerPath || "",
      });
      await refreshMe();
      toast.success("Profile updated");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={save} className="max-w-3xl">
      <div className="border border-[#2A2A2A] bg-[#121212] mb-6 overflow-hidden">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-gray-500 px-6 pt-6">Profile banner</div>
        <div className="p-6">
          <div className="relative h-48 md:h-64 border border-[#2A2A2A] bg-neutral-900 overflow-hidden">
            {bannerPath ? (
              <img src={fileUrl(bannerPath)} alt="banner preview" className="h-full w-full object-cover" />
            ) : (
              <div className="h-full w-full grid place-items-center text-gray-600 font-mono text-xs uppercase tracking-widest">
                <div className="text-center">
                  <ImagePlus className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  No banner yet — recommended 1600×500
                </div>
              </div>
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent pointer-events-none" />
          </div>
          <div className="flex flex-wrap gap-2 mt-3">
            <input data-testid="banner-file" ref={bannerRef} type="file" accept=".jpg,.jpeg,.png,.webp,.gif,.heic,image/jpeg,image/png,image/webp,image/gif,image/heic,image/*" className="flex-1 min-w-[220px] font-mono text-xs bg-black border border-[#2A2A2A] p-2" />
            <Button data-testid="banner-upload" type="button" onClick={uploadBanner} disabled={busy}
                    className="rounded-none bg-white/10 hover:bg-white/20 text-white font-mono text-xs uppercase h-10 px-4">
              <Upload className="h-4 w-4 mr-2" /> Upload
            </Button>
            {bannerPath && (
              <Button data-testid="banner-clear" type="button" onClick={clearBanner} variant="outline"
                      className="rounded-none border-[#2A2A2A] hover:border-red-500 hover:text-red-500 bg-transparent font-mono text-xs uppercase h-10 px-4">
                <Trash2 className="h-4 w-4 mr-2" /> Clear
              </Button>
            )}
          </div>
          <div className="font-mono text-[10px] text-gray-500 mt-2 uppercase tracking-widest">
            JPG / PNG / WEBP · appears full-width on your artist page
          </div>
        </div>
      </div>

      <div className="border border-[#2A2A2A] bg-[#121212] p-6 space-y-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <AtSign className="h-4 w-4 text-[#FF4500]" />
            <Label className="font-mono text-xs uppercase tracking-widest text-gray-200">Your handle</Label>
          </div>
          <div className="font-mono text-[10px] text-gray-500 mb-2">
            This is what fans see and mention — like <span className="text-gray-300">@{user?.username || "yourname"}</span>.
            Lowercase letters, numbers, and underscores only. 3-32 characters.
          </div>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 font-mono text-sm text-gray-500 pointer-events-none">@</span>
              <Input
                data-testid="profile-handle"
                value={handle}
                onChange={(e) => setHandle(e.target.value.toLowerCase().replace(/\s+/g, ""))}
                maxLength={32}
                className={`rounded-none bg-black border-[#2A2A2A] font-mono h-11 pl-7 pr-9 ${
                  handleCheck.ok === false ? "border-red-500" : ""
                } ${handleCheck.ok === true && !handleCheck.current ? "border-green-500" : ""}`}
              />
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                {handleCheck.busy && <div className="h-3 w-3 border-2 border-[#FF4500] border-t-transparent rounded-full animate-spin" />}
                {!handleCheck.busy && handleCheck.ok === true && !handleCheck.current && (
                  <Check data-testid="handle-ok" className="h-4 w-4 text-green-500" />
                )}
                {!handleCheck.busy && handleCheck.ok === false && (
                  <X data-testid="handle-bad" className="h-4 w-4 text-red-500" />
                )}
              </div>
            </div>
            <Button
              data-testid="profile-handle-save"
              type="button"
              onClick={saveHandle}
              disabled={handleSaving || !handleCheck.ok || handleCheck.current}
              className="rounded-none bg-[#FF4500] hover:bg-[#E53E00] text-black font-mono text-xs uppercase tracking-widest h-11 px-4 disabled:opacity-40"
            >
              {handleSaving ? "..." : "Save handle"}
            </Button>
          </div>
          {handleCheck.ok === false && handleCheck.reason && (
            <div className="font-mono text-[10px] text-red-400 mt-2">{handleCheck.reason}</div>
          )}
          {handleCheck.ok === false && handleCheck.suggestion && handleCheck.suggestion !== handle && (
            <button
              type="button"
              data-testid="handle-suggestion"
              onClick={() => setHandle(handleCheck.suggestion)}
              className="mt-2 font-mono text-[10px] text-[#FF4500] hover:underline"
            >
              Suggested: <span className="text-white">@{handleCheck.suggestion}</span> — tap to use
            </button>
          )}
          {handleCheck.current && (
            <div className="font-mono text-[10px] text-gray-500 mt-2">Your current handle.</div>
          )}
        </div>
        <div className="border-t border-[#2A2A2A] pt-4">
          <Label className="font-mono text-xs uppercase tracking-widest text-gray-400">Display name</Label>
          <Input data-testid="profile-display" value={displayName} onChange={(e) => setDisplayName(e.target.value)}
                 className="mt-2 rounded-none bg-black border-[#2A2A2A] font-mono h-11" />
        </div>
        <div>
          <Label className="font-mono text-xs uppercase tracking-widest text-gray-400">Bio</Label>
          <Textarea data-testid="profile-bio" value={bio} onChange={(e) => setBio(e.target.value)} maxLength={1000} rows={4}
                    className="mt-2 rounded-none bg-black border-[#2A2A2A] font-mono resize-none" />
          <div className="font-mono text-[10px] text-gray-500 mt-1 text-right">{bio.length} / 1000</div>
        </div>
        <div className="flex items-center justify-between border-t border-[#2A2A2A] pt-4">
          <div className="flex items-start gap-3">
            <Mail className="h-4 w-4 text-[#FF4500] mt-0.5" />
            <div>
              <Label className="font-mono text-xs uppercase tracking-widest text-gray-200">Release-note emails</Label>
              <div className="font-mono text-[10px] text-gray-500 mt-1">
                Get a friendly email every time we ship a MeloCraft update. One email per release, unsubscribe any time.
              </div>
            </div>
          </div>
          <Switch data-testid="email-optin-toggle" checked={emailOptin} onCheckedChange={setEmailPref} />
        </div>
      </div>

      <div className="flex flex-wrap gap-3 mt-6">
        <Button data-testid="profile-save" type="submit" disabled={busy}
                className="rounded-none bg-[#FF4500] hover:bg-[#E53E00] text-black font-mono text-xs uppercase tracking-widest h-11 px-8">
          {busy ? "Saving..." : "Save profile"}
        </Button>
        <Link to={`/artist/${user.id}`} target="_blank">
          <Button type="button" variant="outline"
                  className="rounded-none border-[#2A2A2A] hover:border-[#FF4500] hover:text-[#FF4500] bg-transparent font-mono text-xs uppercase tracking-widest h-11 px-6">
            <ExternalLink className="h-4 w-4 mr-2" /> Open my page
          </Button>
        </Link>
      </div>
    </form>
  );
}
